import { useEffect, useState } from 'react'
import { PERMISSIONS } from '../../../../../../constants'
import { outletsStore } from '../../../../../../factories/database/outlets-store'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useUserPermission,
} from '../../../../../../ng-compat/hooks'
import { isCrossLocationFulfilmentRolloutEnabled } from '../../../../../../util/cross-location-fulfilment'

export const useCrossLocationFulfilmentGate = () => {
  const registerManager = useRegisterManager()
  const retailerSettingsManager = useRetailerSettingsManager()
  const hasPermission = useUserPermission(PERMISSIONS.SALE_CROSS_LOCATION)
  const isRolloutEnabled = isCrossLocationFulfilmentRolloutEnabled()
  const isAccountEnabled = retailerSettingsManager.isCrossLocationFulfilmentEnabled()
  const isSourceOutletEligible = registerManager.isSourceOutletCLFEnabled()

  const isPreconditionMet =
    isRolloutEnabled &&
    isAccountEnabled &&
    hasPermission &&
    isSourceOutletEligible

  // LRX-42234: fulfilment is only cross-location when at least one outlet
  // other than the sale outlet provides stock. Outlets come from the local
  // store asynchronously, so COF stays unavailable until the check resolves.
  const [activeOutletRevision, setActiveOutletRevision] = useState(0)
  const [providingOutletRevision, setProvidingOutletRevision] = useState(-1)

  useEffect(() => {
    const handleActiveOutletChange = () => {
      setActiveOutletRevision(revision => revision + 1)
    }

    registerManager.on('activeOutletChange', handleActiveOutletChange)

    return () => {
      registerManager.off('activeOutletChange', handleActiveOutletChange)
    }
  }, [registerManager])

  useEffect(() => {
    if (!isPreconditionMet) {
      setProvidingOutletRevision(-1)
      return
    }

    let isStale = false

    Promise.resolve(outletsStore.all())
      .then(outlets => {
        const activeOutletId = registerManager.getActiveOutlet()?.id

        return registerManager
          .getEligibleCLFTargetOutlets(outlets ?? [])
          .some(outlet => outlet.id !== activeOutletId)
      })
      .catch(() => false)
      .then(hasOutlets => {
        if (!isStale && hasOutlets) {
          setProvidingOutletRevision(activeOutletRevision)
        }
      })

    return () => {
      isStale = true
    }
  }, [activeOutletRevision, isPreconditionMet, registerManager])

  return {
    canUseCrossLocationFulfilment:
      isPreconditionMet && providingOutletRevision === activeOutletRevision,
    isRolloutEnabled,
    isAccountEnabled,
    hasPermission,
    isSourceOutletEligible,
  }
}
